// Step 1: where the Bloch states come from - a finished DFT run of yours (route A: the server runs the nscf,
// pw2wannier90.x and wannier90.x, reusing the run's kept charge density), your own Wannier90 interface files
// (route B: .win, .amn, .mmn, .eig), or a finished Wannier90 model (route C: _hr.dat with the .wout).
import { Alert, Anchor, Badge, Button, FileButton, Group, Loader, NumberInput, SegmentedControl, Select, Stack, Table, Tabs, Text,
  TextInput } from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { Link } from "react-router";
import { ErrorBox, notifyError } from "../../components/Errors";
import { formatDate, get, uploadFile, type ProjectDetail, type StoredFile } from "../../lib/api";
import { modulePath } from "../../lib/modules";
import type { DftRun } from "../../lib/qe";
import type { WannierMemory, WannierSource, WSpin } from "../../lib/wannier";
import { filesProblems } from "../../lib/wannier";
import type { Remember } from "./WannierWorkspace";
import { RouteBHelp, RouteCHelp } from "./Help";

const SPINS = [{ value: "none", label: "No spin" }, { value: "collinear", label: "Collinear (up / down)" }, { value: "spinor", label: "Spinors" }];

export function SourceStep({ project, mem, remember, onNext }: {
  project: ProjectDetail; mem: WannierMemory; remember: Remember; onNext: () => void;
}) {
  return (
    <Stack gap="md" mt="md">
      <Tabs value={mem.source} onChange={(v) => v && remember({ source: v as WannierSource })}>
        <Tabs.List>
          <Tabs.Tab value="dft">From a DFT run</Tabs.Tab>
          <Tabs.Tab value="files">Your interface files</Tabs.Tab>
          <Tabs.Tab value="model">A finished Wannier90 model</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="dft" pt="sm"><DftRunPicker mem={mem} remember={remember} onNext={onNext} /></Tabs.Panel>
        <Tabs.Panel value="files" pt="sm"><InterfaceFiles project={project} mem={mem} remember={remember} onNext={onNext} /></Tabs.Panel>
        <Tabs.Panel value="model" pt="sm"><ModelFiles project={project} mem={mem} remember={remember} onNext={onNext} /></Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

function DftRunPicker({ mem, remember, onNext }: { mem: WannierMemory; remember: Remember; onNext: () => void }) {
  const runs = useQuery({ queryKey: ["dft-runs"], queryFn: () => get<DftRun[]>("/dft/runs") });
  if (runs.isLoading) return <Loader size="sm" />;
  if (runs.error) return <ErrorBox error={runs.error} />;
  const list = runs.data ?? [];
  if (mem.dft_run && !list.some((r) => r.job_id === mem.dft_run))      // an illustrative project's own DFT run
    return (
      <Stack gap="xs" data-testid="own-dft-run">
        <Text size="sm">DFT run: <b>{mem.dft_label ?? "the DFT run of this project"}</b> - the illustrative project's run, copied into
          this project with its charge density (a new Wannier run starts at the nscf).</Text>
        <Group justify="flex-end"><Button onClick={onNext}>Next: mesh</Button></Group>
      </Stack>
    );
  if (!list.length)
    return (
      <Alert color="blue" data-testid="no-dft-runs">No finished DFT runs yet. Run a band structure in a{" "}
        <Anchor component={Link} to={modulePath("dft")}>DFT project</Anchor> first (with orbital projections, so the windows can be
        suggested), or use your own interface files.</Alert>
    );
  const label = (r: DftRun) => `${r.project_name} · ${r.formula ?? r.seed ?? "run"}${r.created_at ? ` (${formatDate(r.created_at)})` : ""}`;
  const chosen = list.find((r) => r.job_id === mem.dft_run);
  return (
    <Stack gap="xs">
      <Text size="sm" c="dimmed">The server runs a non-self-consistent calculation on the uniform mesh from the run's kept charge density
        (no new SCF), then pw2wannier90.x and wannier90.x. The run's band structure is used to check the windows and to judge the model.</Text>
      <Select label="DFT run" data={list.map((r) => ({ value: r.job_id, label: label(r) }))} value={mem.dft_run ?? null} searchable
        allowDeselect={false} data-testid="dft-run-select"
        onChange={(v) => { const r = list.find((x) => x.job_id === v); if (r) remember({ dft_run: r.job_id, dft_label: r.formula ?? r.seed ?? "DFT run", projections: {}, windows: null,
          spin: r.soc ? "spinor" : r.magnetic ? "collinear" : "none" }); }} />
      {chosen && (
        <Table withTableBorder fz="sm" data-testid="dft-run-summary">
          <Table.Tbody>
            <Table.Tr><Table.Td>Material</Table.Td><Table.Td>{chosen.formula} {chosen.spacegroup ? `(${chosen.spacegroup})` : ""}</Table.Td></Table.Tr>
            <Table.Tr><Table.Td>Bands, k-points on the path</Table.Td><Table.Td>{chosen.bands} bands · {chosen.kpoints} k-points</Table.Td></Table.Tr>
            <Table.Tr><Table.Td>Fermi energy, gap</Table.Td><Table.Td>{chosen.fermi_energy_eV?.toFixed(3)} eV · {chosen.gap_eV ? `${chosen.gap_eV.toFixed(2)} eV` : "metal"}</Table.Td></Table.Tr>
            <Table.Tr><Table.Td>Spin</Table.Td><Table.Td>{chosen.soc ? "non-collinear / spin-orbit: spinor Wannier functions" : chosen.magnetic ? "collinear magnetic: two channels" : "none"}</Table.Td></Table.Tr>
            <Table.Tr><Table.Td>Orbital projections</Table.Td><Table.Td>{chosen.has_projection ? <Badge color="green" variant="light">yes</Badge>
              : <Badge color="orange" variant="light">no - the windows cannot be suggested</Badge>}</Table.Td></Table.Tr>
          </Table.Tbody>
        </Table>
      )}
      {chosen?.soc && <Alert color="blue" p="xs" data-testid="spinor-note"><Text size="xs">Spinor Wannier functions: each projection gives two
        (one per spin along z), so the count doubles. The run also writes the spin matrices (pw2wannier90's .spn), so a Zeeman field on the
        model uses each function's own spin - with spin-orbit coupling they are not purely up or down.</Text></Alert>}
      <Group justify="flex-end"><Button onClick={onNext} disabled={!chosen}>Next: mesh</Button></Group>
    </Stack>
  );
}

/** A file of the project for one role: chosen from the uploads, or uploaded here. */
function FileRole({ project, label, value, onChange, accept, required = true }: {
  project: ProjectDetail; label: string; value?: string; onChange: (id: string | undefined) => void; accept?: string; required?: boolean;
}) {
  const qc = useQueryClient();
  const uploads = useMemo(() => (project.files ?? []).filter((f: StoredFile) => f.status === "ready"), [project.files]);
  const up = useMutation({
    mutationFn: (file: File) => uploadFile(project.id, file),
    onSuccess: (f) => { onChange(f.id); qc.invalidateQueries({ queryKey: ["project", project.id] }); },
    onError: (e) => notifyError(e),
  });
  return (
    <Group gap="xs" align="flex-end" wrap="nowrap">
      <Select style={{ flex: 1 }} size="xs" label={label} placeholder={required ? "choose a file" : "none"} searchable clearable={!required}
        data={uploads.map((f) => ({ value: f.id, label: f.name }))} value={value ?? null} onChange={(v) => onChange(v ?? undefined)} />
      <FileButton onChange={(f) => f && up.mutate(f)} accept={accept}>
        {(props) => <Button size="xs" variant="default" loading={up.isPending} {...props}>Upload</Button>}
      </FileButton>
    </Group>
  );
}

/** Pick the uploads by their names (newest first): x.win, x.amn(.gz), ... */
export function guessRoles(files: StoredFile[], roles: string[], spin?: "up" | "dn"): Record<string, string> {
  const out: Record<string, string> = {};
  const sorted = [...files].filter((f) => f.status === "ready").sort((a, b) => b.created_at.localeCompare(a.created_at));
  for (const r of roles) {
    const re = new RegExp(`${spin ? `[._-](${spin === "up" ? "up" : "dn|down"})` : ""}\\.${r}(\\.gz)?$`, "i");
    const f = sorted.find((x) => re.test(x.name) && (spin || !/[._-](up|dn|down)\.\w+(\.gz)?$/i.test(x.name)));
    if (f) out[r] = f.id;
  }
  return out;
}

function InterfaceFiles({ project, mem, remember, onNext }: { project: ProjectDetail; mem: WannierMemory; remember: Remember; onNext: () => void }) {
  const roles = ["win", "amn", "mmn", "eig"] as const;
  const spin = (mem.spin ?? "none") as WSpin;
  const groups: ["files" | "files_dn", string][] = spin === "collinear" ? [["files", " (spin up)"], ["files_dn", " (spin down)"]] : [["files", ""]];
  const problems = filesProblems({ ...mem, source: "files" });
  return (
    <Stack gap="sm" data-testid="interface-files">
      <Text size="sm" c="dimmed">From your own DFT run (Quantum ESPRESSO's pw2wannier90.x, VASP's LWANNIER90, ...): the .win and the
        .amn, .mmn, .eig it was made with (gzip-compressed is fine). The headers are checked against the .win before anything runs;
        write_hr and write_xyz are switched on, nothing else of the .win is changed. With spinors, the .spn
        (pw2wannier90.x with write_spn) gives the model its spin matrices.</Text>
      <SegmentedControl size="xs" value={spin} data={SPINS} onChange={(v) => remember({ spin: v as WSpin })} />
      {groups.map(([g, suffix]) => (
        <Stack key={g} gap={4}>
          {roles.map((r) => (
            <FileRole key={r} project={project} label={`.${r}${suffix}`} value={(mem[g] ?? {})[r]}
              onChange={(id) => remember({ [g]: { ...(mem[g] ?? {}), [r]: id } } as Partial<WannierMemory>)} />
          ))}
          {spin === "spinor" && (
            <FileRole project={project} label=".spn (optional: the spin matrices)" value={mem.files?.spn} required={false}
              onChange={(id) => remember({ files: { ...(mem.files ?? {}), spn: id } })} />
          )}
          <Button size="compact-xs" variant="subtle" w="fit-content"
            onClick={() => remember({ [g]: { ...(mem[g] ?? {}), ...guessRoles(project.files, [...roles, ...(spin === "spinor" ? ["spn"] : [])], g === "files_dn" ? "dn" : spin === "collinear" ? "up" : undefined) } } as Partial<WannierMemory>)}>
            Pick the uploads by name
          </Button>
        </Stack>
      ))}
      <Group grow>
        <NumberInput size="xs" label="Fermi energy of the DFT run" rightSection={<Text size="xs" c="dimmed">eV</Text>} decimalScale={6}
          value={mem.efermi ?? ""} onChange={(v) => remember({ efermi: v === "" ? null : Number(v) })} />
        <TextInput size="xs" label="Seed name" value={mem.seed} onChange={(e) => remember({ seed: e.currentTarget.value })} />
      </Group>
      {problems.length > 0 && <Alert color="gray" p="xs"><Text size="xs">{problems.join(" ")}</Text></Alert>}
      <RouteBHelp />
      <Group justify="flex-end"><Button onClick={onNext} disabled={problems.length > 0}>Next: run</Button></Group>
    </Stack>
  );
}

function ModelFiles({ project, mem, remember, onNext }: { project: ProjectDetail; mem: WannierMemory; remember: Remember; onNext: () => void }) {
  const md = mem.model ?? {};
  const set = (k: keyof NonNullable<WannierMemory["model"]>) => (id: string | undefined) => remember({ model: { ...md, [k]: id } });
  const problems = filesProblems({ ...mem, source: "model" });
  return (
    <Stack gap="sm" data-testid="model-files">
      <Text size="sm" c="dimmed">A Wannier90 run done elsewhere: its _hr.dat and the .wout (or the .win and _centres.xyz) become a
        tight-binding model file for NEGF Transport and Properties. No programs run.</Text>
      <SegmentedControl size="xs" value={mem.spin ?? "none"} data={SPINS} onChange={(v) => remember({ spin: v as WSpin })} />
      <FileRole project={project} label={mem.spin === "collinear" ? "_hr.dat (spin up)" : "_hr.dat"} value={md.hr} onChange={set("hr")} />
      {mem.spin === "collinear" && <FileRole project={project} label="_hr.dat (spin down)" value={md.hr_dn} onChange={set("hr_dn")} />}
      <FileRole project={project} label=".wout" value={md.wout} onChange={set("wout")} required={false} />
      <FileRole project={project} label=".win (without a .wout)" value={md.win} onChange={set("win")} required={false} />
      <FileRole project={project} label="_centres.xyz (without a .wout)" value={md.centres} onChange={set("centres")} required={false} />
      <FileRole project={project} label="_wsvec.dat" value={md.wsvec} onChange={set("wsvec")} required={false} />
      {mem.spin !== "collinear" && <FileRole project={project} label="_tb.dat (optional: position matrices for electric fields)" value={md.tb}
        onChange={set("tb")} required={false} />}
      <Group grow>
        <NumberInput size="xs" label="Fermi energy" rightSection={<Text size="xs" c="dimmed">eV</Text>} decimalScale={6}
          value={mem.efermi ?? ""} onChange={(v) => remember({ efermi: v === "" ? null : Number(v) })} />
        <NumberInput size="xs" label="Drop hoppings below" description="0 keeps all" rightSection={<Text size="xs" c="dimmed">eV</Text>}
          min={0} step={0.001} decimalScale={6} value={mem.hop_cutoff ?? 0} onChange={(v) => remember({ hop_cutoff: Number(v) || 0 })} />
        <TextInput size="xs" label="Seed name" value={mem.seed} onChange={(e) => remember({ seed: e.currentTarget.value })} />
      </Group>
      {problems.length > 0 && <Alert color="gray" p="xs"><Text size="xs">{problems.join(" ")}</Text></Alert>}
      <RouteCHelp />
      <Group justify="flex-end"><Button onClick={onNext} disabled={problems.length > 0}>Next: build</Button></Group>
    </Stack>
  );
}
